import { alpha, Box } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { AlertRuleScreenProps } from 'components/alert-rule-popup/index';
import { Trans, useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import { AlertRulePrepareFormData } from 'components/alert-rule-popup/AlertRuleDataProcessor';
import { alertRuleAPI } from 'api/alert-wizard';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';

interface AlertRulePreviewProps extends AlertRuleScreenProps {
    onChange?: (text: any) => void;
}

export default function AlertRulePreview(props: AlertRulePreviewProps) {
    const { alertRuleData, wizardData } = props;
    const [showErrorType, setShowErrorType] = useState('');
    const [previewContent, setPreviewContent] = useState<any>(null);
    const previewContainerRef = useRef<any | null>(null);
    const [showGeneratingLoading, setShowGeneratingLoading] = useState<boolean>(true);

    const { t } = useTranslation(['components/alert-rule-popup/alert_rule_popup']);

    useEffect(() => {
        setShowGeneratingLoading(true);
        setShowErrorType('');
        setPreviewContent('');
        renderAlertPreview();
    }, [
        alertRuleData.preview_for,
        alertRuleData.visualization_type,
        alertRuleData.visualization_element_id,
        alertRuleData.subject_format_email,
        alertRuleData.subject_format_sms,
        alertRuleData.display_format,
        alertRuleData.additional_message,
        alertRuleData.match_measurement_time_ind,
        wizardData.isPhoneAlert, //isChangedDeliveryTypes
        wizardData.isSmsAlert, //isChangedDeliveryTypes
    ]);

    const formatPreviewContent = (responseData: any) => {
        if (!responseData) {
            setPreviewContent(null);
            return false;
        } else {
            setPreviewContent(
                <>
                    {responseData.label && (
                        <>
                            <Box className="custom_label" sx={{ padding: 2, pb: 0.5 }}>
                                {responseData.label}
                            </Box>
                            <Box
                                className="alert_p_subjects"
                                sx={{
                                    color: 'primary.main',
                                    fontWeight: 700,
                                    padding: 2,
                                    pt: 0,
                                    borderBottom: '1px solid',
                                    borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                }}
                            >
                                {responseData.subject}
                            </Box>
                        </>
                    )}
                    {responseData.preview && (
                        <Box className="alert_p_preview" sx={{ padding: 2 }}>
                            <Box
                                sx={{
                                    '#scalable_part': {
                                        width: '100%',
                                        height: 'auto',
                                        overflow: 'hidden',
                                    },
                                    '#mainWrapTableEmail': {
                                        transformOrigin: 'top left',
                                        pointerEvents: 'none',
                                    },
                                }}
                                className="email_preview"
                                id="scalable_part"
                                dangerouslySetInnerHTML={{ __html: responseData.preview }}
                            />
                        </Box>
                    )}
                </>,
            );
        }
    };

    const renderAlertPreview = () => {
        if (alertRuleData.preview_for == 'push' && !wizardData.isPhoneAlert) {
            setShowErrorType('push');
            setShowGeneratingLoading(false);
        } else if (alertRuleData.preview_for == 'sms' && !wizardData.isSmsAlert) {
            setShowErrorType('sms');
            setShowGeneratingLoading(false);
        } else {
            const data = AlertRulePrepareFormData(alertRuleData, wizardData);
            //sv = $('#dPrevSegmentValue').val();
            data.segment_value_id =
                alertRuleData.segment_value_id > 0 ? alertRuleData.segment_value_id : data.segment_value_id;
            alertRuleAPI
                .getPreviewData(alertRuleData, alertRuleData.preview_for)
                .then((response) => {
                    formatPreviewContent(response.data);
                    setTimeout(() => {
                        contentScaleForPreview();
                    }, 100);
                    setShowGeneratingLoading(false);
                })
                .catch(() => {
                    setShowGeneratingLoading(false);
                });
        }
    };

    const contentScaleForPreview = () => {
        const container = previewContainerRef.current;
        if (!container) return false;

        const containerForPreview = container.querySelector('#scalable_part');
        const contentForScale = containerForPreview.querySelector('#mainWrapTableEmail');
        if (!containerForPreview || !contentForScale) return false;

        let currentContentHeight = contentForScale.offsetHeight;
        const ratio = containerForPreview.offsetWidth / contentForScale.offsetWidth;

        if (containerForPreview.offsetWidth < contentForScale.offsetWidth) {
            containerForPreview.classList.add('eRatioWasSet');
            contentForScale.style.transform = 'scale(' + ratio + ')';
            containerForPreview.style.height = contentForScale.offsetHeight * ratio + 'px';
        }

        const img = contentForScale.querySelector('img');

        const loadedCallback = () => {
            if (
                containerForPreview.classList.contains('eRatioWasSet') &&
                currentContentHeight != contentForScale.offsetHeight
            ) {
                containerForPreview.style.height = contentForScale.offsetHeight * ratio + 'px';
                currentContentHeight = contentForScale.offsetHeight;
            }
        };

        if (img) {
            if (img.complete) {
                loadedCallback();
            } else {
                img.addEventListener('load', loadedCallback);
            }
        }
    };

    const maskLoadingBlock = (
        <LoadingPlaceholder
            relativeToParent
            sx={{
                backgroundColor: (theme) => alpha(theme.palette.background.default, 0.3),
                zIndex: 5,
                '.loading-placeholder__icon': {
                    width: '20px !important',
                    height: '20px !important',
                    color: (theme) => alpha(theme.palette.text.primary, 0.4),
                },
                '.loading-placeholder__container': {
                    color: 'text.primary',
                    textAlign: 'center',
                    backgroundColor: (theme) => theme.palette.background.default,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                    padding: 2,
                },
            }}
        >
            <Box>{t('form.preview.loading_msg')}</Box>
        </LoadingPlaceholder>
    );

    return (
        <Box ref={previewContainerRef}>
            {showGeneratingLoading && maskLoadingBlock}
            {showErrorType == 'push' && (
                <Alert severity={'error'} variant={'standard'}>
                    <Box component={'strong'}>{t('form.preview.pushErrorText_1')}</Box>
                    <Box>{t('form.preview.pushErrorText_2')}</Box>
                </Alert>
            )}
            {showErrorType == 'sms' && (
                <Alert severity={'info'} variant={'standard'}>
                    <Trans t={t} i18nKey="form.preview.smsErrorText">
                        placeholder_before_link_text
                        <a href="/notification/receiving" target="_blank">
                            placeholder_link_text
                        </a>
                    </Trans>
                </Alert>
            )}
            {showErrorType == '' && previewContent && <Box>{previewContent}</Box>}
        </Box>
    );
}
